import { useFormContext } from 'react-hook-form'
import { DockerImageInput } from '@/components/form/docker-image-input'
import { PrivateGitSelector } from '@/components/form/private-git-selector'
import { PublicGitInput } from '@/components/form/public-git-input'
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/lib/utils'
import { BitBucketLogo, GitHubLogo, GitLabLogo } from '@/logos'
import { DockerLogo } from '@/logos/docker'
import { GitLogo } from '@/logos/git'
import type { FC } from 'react'

type Props = {
  showBranchSelector?: boolean
}

export const AppSourceSelector: FC<Props> = ({ showBranchSelector = true }) => {
  const form = useFormContext()

  const { watch } = form

  const source = watch('source')

  return (
    <FormField
      control={form.control}
      name="source"
      render={({ field: { value, onChange } }) => (
        <>
          <FormItem>
            <FormControl>
              <RadioGroup
                className="grid grid-cols-3 gap-3"
                onValueChange={onChange}
                value={value}
              >
                <Label
                  className={cn(
                    'relative has-[[data-state=checked]]:border-ring has-[[data-state=checked]]:bg-input/20',
                    'flex flex-col items-center justify-center gap-3 rounded-lg border px-3 py-6'
                  )}
                  key="privateGit"
                >
                  <RadioGroupItem
                    value="privateGit"
                    id="privateGit"
                    className="absolute top-3 left-3 data-[state=checked]:border-primary"
                  />
                  <div className="grid gap-2 text-center place-items-center font-normal">
                    <div className="flex items-center gap-2">
                      <GitHubLogo className="size-4" />
                      <BitBucketLogo className="size-4" />
                      <GitLabLogo className="size-4" />
                    </div>
                    <div className="font-medium">Private GIT repository</div>
                  </div>
                </Label>
                <Label
                  className={cn(
                    'relative has-[[data-state=checked]]:border-ring has-[[data-state=checked]]:bg-input/20',
                    'flex flex-col items-center justify-center gap-3 rounded-lg border p-3'
                  )}
                  key="publicGit"
                >
                  <RadioGroupItem
                    value="publicGit"
                    id="publicGit"
                    className="absolute top-3 left-3 data-[state=checked]:border-primary"
                  />
                  <div className="grid w-full place-items-center gap-2 text-center font-normal">
                    <GitLogo className="size-4" />
                    <div className="flex items-center gap-2">
                      <div className="font-medium">Public GIT repository</div>
                    </div>
                  </div>
                </Label>
                <Label
                  className={cn(
                    'relative has-[[data-state=checked]]:border-ring has-[[data-state=checked]]:bg-input/20',
                    'flex flex-col items-center justify-center gap-3 rounded-lg border p-3'
                  )}
                  key="dockerImage"
                >
                  <RadioGroupItem
                    value="dockerImage"
                    id="dockerImage"
                    className="absolute top-3 left-3 data-[state=checked]:border-primary"
                  />
                  <div className="grid w-full place-items-center gap-2 text-center font-normal">
                    <DockerLogo className="size-4" />
                    <div className="flex items-center gap-2">
                      <div className="font-medium">Docker image</div>
                    </div>
                  </div>
                </Label>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
          {source === 'privateGit' ? (
            <PrivateGitSelector showBranchSelector={showBranchSelector} />
          ) : null}
          {source === 'publicGit' ? (
            <PublicGitInput showBranchSelector={showBranchSelector} />
          ) : null}
          {source === 'dockerImage' ? <DockerImageInput /> : null}
        </>
      )}
    />
  )
}
